import classNames from 'classnames';
import { observer } from 'mobx-react';
import React from 'react';
import { ClientSideRender } from '@udemy/design-system-utils';
import { useI18n } from '@udemy/i18n';
import { ModalLanguageSelector, LanguageSelectorButton } from '@udemy/language-selector';
import { Button } from '@udemy/react-core-components';
import { ContentGrid, ContentArea, ContentGridItem } from '@udemy/react-layout-components';
import { Accordion } from '@udemy/react-reveal-components';
import { serverOrClient } from '@udemy/shared-utils';
import { useUDData } from '@udemy/ud-data';
import { LimitedConsumptionTrial } from '../../external/organization-trial/limited-consumption-trial/limited-consumption-trial.react-component';
import { useIsFeatureEnabled } from '../../external/utils/use-is-feature-enabled';
import { Links } from '../links/links.react-component';
import { Logos } from '../logos/logos.react-component';
import { TeachOnUdemyBanner } from '../teach-on-udemy-banner/teach-on-udemy-banner.react-component';
import { UFBNotice } from '../ufb-notice/ufb-notice.react-component';
import { FooterContextProvider, useFooterContext } from './footer-context';
const FooterImplementation = observer(({
  useLangPrefixedUrls = false
}) => {
  var _footerStore$browseCe, _footerStore$footerEx, _footerStore$browseTo, _footerStore$browseTo2, _footerStore$footerEx2, _footerStore$ufbNotic, _footerStore$footerEx3, _footerStore$footerEx4, _footerStore$footerEx5, _footerStore$footerEx6, _footerStore$footerEx7, _footerStore$structur, _footerStore$footerEx8, _footerStore$footerEx9, _footerStore$footerEx0, _footerStore$bottomLi;
  const {
    footerStore,
    limitedConsumptionTrial
  } = useFooterContext();
  const {
    Config
  } = useUDData();
  const {
    gettext
  } = useI18n();
  const isFeatureEnabled = useIsFeatureEnabled();
  const [isMounted, setIsMounted] = React.useState(false);
  React.useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);
  const shouldShowTeachBanner = isMounted && !Config.brand.has_organization && !footerStore.isJpFooter && !serverOrClient.global.location.pathname.startsWith('/teaching/') && !!footerStore.user && !footerStore.user.has_instructor_intent;
  const shouldShowUfbNotice = !Config.brand.has_organization && !footerStore.isJpFooter && isFeatureEnabled('ufb_notices_footer.top_com') && footerStore.ufbNotice.link && footerStore.ufbNotice.placement;
  const ubIsLimitedConsumptionTrial = Config.brand.has_organization && Config.brand.organization.is_limited_consumption_trial;

  // Assuming that there are more topic links than cert links, evenly interleave the cert links throughout the topic links,
  // starting with a cert link.
  const interleaveLinks = (exploreLinks, topicLinks) => {
    if (!(exploreLinks !== null && exploreLinks !== void 0 && exploreLinks.length)) {
      return topicLinks;
    }
    if (!(topicLinks !== null && topicLinks !== void 0 && topicLinks.length)) {
      return exploreLinks;
    }
    const interleavedLinks = [...topicLinks];
    const step = topicLinks.length / exploreLinks.length;
    exploreLinks.forEach((val, idx) => {
      const insertionIndex = Math.ceil(step * idx);
      interleavedLinks.splice(insertionIndex + idx, 0, val);
    });
    return interleavedLinks;
  };

  // Clone the footer store's browseCertificationLinks because we're about to mutate it
  const browseExploreLinks = [...((_footerStore$browseCe = footerStore.browseCertificationLinks) !== null && _footerStore$browseCe !== void 0 ? _footerStore$browseCe : [])];

  // If Academies is enabled, replace an aggregator browse link with the academies
  // browse link;
  if ((_footerStore$footerEx = footerStore.footerExperimentFeatures) !== null && _footerStore$footerEx !== void 0 && _footerStore$footerEx.isAcademiesEnabled) {
    var _footerStore$careerAc;
    if (browseExploreLinks && (_footerStore$careerAc = footerStore.careerAcceleratorLinks) !== null && _footerStore$careerAc !== void 0 && _footerStore$careerAc.length) {
      browseExploreLinks.shift();
      browseExploreLinks.unshift({
        title: gettext('In-demand Careers'),
        links: footerStore.careerAcceleratorLinks
      });
    }
  }
  const gridBrowseLinks = interleaveLinks(browseExploreLinks, footerStore.browseTopicLinks);
  const accordionBrowseLinks = browseExploreLinks === null || browseExploreLinks === void 0 ? void 0 : browseExploreLinks.concat((_footerStore$browseTo = (_footerStore$browseTo2 = footerStore.browseTopicLinks) === null || _footerStore$browseTo2 === void 0 ? void 0 : _footerStore$browseTo2.slice()) !== null && _footerStore$browseTo !== void 0 ? _footerStore$browseTo : []);
  return /*#__PURE__*/React.createElement("footer", {
    className: classNames('ud-footer', {
      'ud-footer-initially-hidden': !!footerStore.hideFooterUntilContentReady,
      'ud-ufb-trial-footer': ubIsLimitedConsumptionTrial
    }),
    "data-purpose": "footer"
  }, shouldShowTeachBanner && /*#__PURE__*/React.createElement(TeachOnUdemyBanner, {
    isAggregatorEnabled: (_footerStore$footerEx2 = footerStore.footerExperimentFeatures) === null || _footerStore$footerEx2 === void 0 ? void 0 : _footerStore$footerEx2.isAggregatorEnabled
  }), shouldShowUfbNotice && /*#__PURE__*/React.createElement(UFBNotice, {
    link: footerStore.ufbNotice.link,
    placement: (_footerStore$ufbNotic = footerStore.ufbNotice.placement) !== null && _footerStore$ufbNotic !== void 0 ? _footerStore$ufbNotic : 'footer',
    isOnsiteRequestDemo: footerStore.ufbNotice.isOnsiteRequestDemo,
    isAggregatorEnabled: (_footerStore$footerEx3 = footerStore.footerExperimentFeatures) === null || _footerStore$footerEx3 === void 0 ? void 0 : _footerStore$footerEx3.isAggregatorEnabled
  }), /*#__PURE__*/React.createElement(ClientSideRender, null, /*#__PURE__*/React.createElement(ContentArea, null, ((_footerStore$footerEx4 = footerStore.footerExperimentFeatures) === null || _footerStore$footerEx4 === void 0 ? void 0 : _footerStore$footerEx4.isAggregatorEnabled) && (gridBrowseLinks === null || gridBrowseLinks === void 0 ? void 0 : gridBrowseLinks.length) && /*#__PURE__*/React.createElement("div", {
    className: classNames('footer-section', 'footer-section-structured-links', {
      'footer-section-med-background': (_footerStore$footerEx5 = footerStore.footerExperimentFeatures) === null || _footerStore$footerEx5 === void 0 ? void 0 : _footerStore$footerEx5.isAggregatorEnabled,
      'footer-section-med-border': (_footerStore$footerEx6 = footerStore.footerExperimentFeatures) === null || _footerStore$footerEx6 === void 0 ? void 0 : _footerStore$footerEx6.isAggregatorEnabled
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: "structured-links"
  }, /*#__PURE__*/React.createElement("div", {
    className: "structured-links-heading"
  }, /*#__PURE__*/React.createElement("span", {
    className: "ud-heading-serif-lg"
  }, gettext('Explore top skills and certifications'))), /*#__PURE__*/React.createElement(ContentGrid, {
    className: "hide-on-mobile"
  }, gridBrowseLinks.map((structuredLinkColumn, i) => {
    return /*#__PURE__*/React.createElement(ContentGridItem, {
      key: i,
      smSpan: 1,
      mdSpan: 3,
      defaultSpan: 3
    }, /*#__PURE__*/React.createElement("div", {
      className: "structured-links-column"
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames('structured-links-column-heading', 'ud-heading-md')
    }, structuredLinkColumn.title), /*#__PURE__*/React.createElement(Links, {
      className: "structured-links-list",
      linkColumns: [structuredLinkColumn.links]
    })));
  })), /*#__PURE__*/React.createElement(Accordion, {
    size: "medium",
    className: "hide-on-desktop",
    name: "footer-browse-links-accordion"
  }, accordionBrowseLinks === null || accordionBrowseLinks === void 0 ? void 0 : accordionBrowseLinks.map((structuredLinkColumn, i) => {
    return /*#__PURE__*/React.createElement(Accordion.Panel, {
      title: /*#__PURE__*/React.createElement("div", {
        className: "accordion-title"
      }, structuredLinkColumn.title),
      key: i,
      toggleStrategy: "add-remove",
      className: "browse-links-panel"
    }, /*#__PURE__*/React.createElement(Links, {
      className: "structured-links-list",
      linkColumns: [structuredLinkColumn.links]
    }));
  })))))), !((_footerStore$footerEx7 = footerStore.footerExperimentFeatures) !== null && _footerStore$footerEx7 !== void 0 && _footerStore$footerEx7.isAggregatorEnabled) ? /*#__PURE__*/React.createElement("div", {
    className: classNames('footer-section', 'footer-section-main')
  }, /*#__PURE__*/React.createElement("div", {
    className: "links-and-language-selector"
  }, /*#__PURE__*/React.createElement("div", {
    className: "language-selector-container"
  }, /*#__PURE__*/React.createElement(ModalLanguageSelector, {
    uiRegion: "footer",
    useLangPrefixedUrls: useLangPrefixedUrls,
    trigger: /*#__PURE__*/React.createElement(LanguageSelectorButton, {
      udStyle: "white-outline",
      "data-testid": "language-selector-button"
    })
  })), /*#__PURE__*/React.createElement(Links, {
    linkColumns: footerStore.linkColumns
  })), /*#__PURE__*/React.createElement("div", {
    className: "logo-and-copyright"
  }, /*#__PURE__*/React.createElement("div", {
    className: "logo-container",
    "data-testid": "logo-container"
  }, /*#__PURE__*/React.createElement(Logos, {
    isJpFooter: footerStore.isJpFooter
  })), /*#__PURE__*/React.createElement("div", {
    className: "copyright-container ud-text-xs"
  }, `\u00A9 ${new Date().getFullYear()} Udemy, Inc.`)), Config.brand.has_organization && Config.brand.organization.is_enterprise_china && /*#__PURE__*/React.createElement("div", {
    className: "sanjieke ud-text-xs"
  }, /*#__PURE__*/React.createElement(Button, {
    componentClass: "a",
    udStyle: "link",
    className: "white-link",
    typography: "ud-text-xs",
    href: "http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=11010102005771",
    target: "_blank",
    rel: "noopener noreferrer nofollow"
  }, '京公网安备 11010102005771号'), /*#__PURE__*/React.createElement("span", {
    className: "spacing"
  }, 'ICP证：京B2-20190453'), /*#__PURE__*/React.createElement(Button, {
    componentClass: "a",
    udStyle: "link",
    typography: "ud-text-xs",
    className: "white-link",
    href: "http://beian.miit.gov.cn/",
    target: "_blank",
    rel: "noopener noreferrer nofollow"
  }, '京ICP备16034860号'))) : /*#__PURE__*/React.createElement(ClientSideRender, null, /*#__PURE__*/React.createElement(ContentArea, null, /*#__PURE__*/React.createElement("div", {
    className: classNames('footer-section', 'footer-section-main', 'footer-section-dark-background', 'footer-section-no-border', 'footer-section-structured-links')
  }, /*#__PURE__*/React.createElement("div", {
    className: "links-and-language-selector"
  }, (_footerStore$structur = footerStore.structuredLinkColumns) !== null && _footerStore$structur !== void 0 && _footerStore$structur.length ? /*#__PURE__*/React.createElement("div", {
    className: "structured-links"
  }, /*#__PURE__*/React.createElement(ContentGrid, null, footerStore.structuredLinkColumns.map((structuredLinkColumn, i) => {
    return /*#__PURE__*/React.createElement(ContentGridItem, {
      key: i,
      smSpan: 1,
      mdSpan: 3,
      defaultSpan: 3
    }, /*#__PURE__*/React.createElement("div", {
      className: "structured-links-column"
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames('structured-links-column-heading', 'ud-heading-md')
    }, structuredLinkColumn.title), /*#__PURE__*/React.createElement(Links, {
      className: "structured-links-list",
      linkColumns: [structuredLinkColumn.links]
    })));
  }))) : /*#__PURE__*/React.createElement(Links, {
    linkColumns: footerStore.linkColumns
  }))))), isMounted && Config.brand.has_organization && /*#__PURE__*/React.createElement("div", {
    className: "ud-ufb-fixed-message-container"
  }, ubIsLimitedConsumptionTrial && limitedConsumptionTrial && /*#__PURE__*/React.createElement(LimitedConsumptionTrial, null)), (_footerStore$footerEx8 = footerStore.footerExperimentFeatures) !== null && _footerStore$footerEx8 !== void 0 && _footerStore$footerEx8.isAggregatorEnabled ? /*#__PURE__*/React.createElement("div", {
    className: classNames('footer-section', {
      'footer-section-dark-background': (_footerStore$footerEx9 = footerStore.footerExperimentFeatures) === null || _footerStore$footerEx9 === void 0 ? void 0 : _footerStore$footerEx9.isAggregatorEnabled,
      'footer-section-light-border': (_footerStore$footerEx0 = footerStore.footerExperimentFeatures) === null || _footerStore$footerEx0 === void 0 ? void 0 : _footerStore$footerEx0.isAggregatorEnabled
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: "bottom-container"
  }, /*#__PURE__*/React.createElement("div", {
    className: "logo-and-copyright-bottom"
  }, /*#__PURE__*/React.createElement("div", {
    className: "logo-container",
    "data-testid": "logo-container"
  }, /*#__PURE__*/React.createElement(Logos, {
    isJpFooter: footerStore.isJpFooter
  })), /*#__PURE__*/React.createElement("div", {
    className: "copyright-container ud-text-sm"
  }, `\u00A9 ${new Date().getFullYear()} Udemy, Inc.`)), ((_footerStore$bottomLi = footerStore.bottomLinks) === null || _footerStore$bottomLi === void 0 ? void 0 : _footerStore$bottomLi.length) && /*#__PURE__*/React.createElement("div", {
    className: "bottom-links-container"
  }, /*#__PURE__*/React.createElement(Links, {
    className: "bottom-links",
    linkColumns: [footerStore.bottomLinks]
  })), /*#__PURE__*/React.createElement("div", {
    className: "language-selector-container-bottom"
  }, /*#__PURE__*/React.createElement(ModalLanguageSelector, {
    uiRegion: "footer",
    useLangPrefixedUrls: useLangPrefixedUrls,
    trigger: /*#__PURE__*/React.createElement(LanguageSelectorButton, {
      udStyle: "link",
      className: "language-selector-button-bottom",
      "data-testid": "language-selector-button"
    })
  })))) : null);
});
export const Footer = ({
  clientProps,
  serverProps,
  limitedConsumptionTrial = false,
  useLangPrefixedUrls = false
}) => {
  return /*#__PURE__*/React.createElement(FooterContextProvider, {
    clientProps: clientProps,
    serverProps: serverProps,
    limitedConsumptionTrial: limitedConsumptionTrial
  }, /*#__PURE__*/React.createElement(FooterImplementation, {
    useLangPrefixedUrls: useLangPrefixedUrls
  }));
};
//# sourceMappingURL=footer.react-component.js.map